import React from "react";
import PropTypes from "prop-types";
import { Card, Grid, Icon, IconButton, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import bodyImageUrl from "../../assets/images/Patient.png";
import NotesUrl from "../../assets/images/FreeDraw.png";

const DiagnosisEntry = ({ entry, isAdmin, onDelete }) => {
  const clinicalFields = [
    { label: "Chief Complaint", value: entry.chiefComplaint },
    { label: "Physical Diagnosis", value: entry.physicalDiagnosis },
    { label: "Medical History", value: entry.medicalHistory },
    { label: "Past History", value: entry.pastHistory },
    { label: "Clinical Findings", value: entry.clinicalFindings },
    { label: "Plan of Treatment", value: entry.finalDiagnosis },
  ];

  const renderLayeredDisplay = (diagramUrl) => {
    if (!diagramUrl) return null;
    return (
      <MDBox
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "700px",
          mx: "auto",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <img
          src={diagramUrl}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            pointerEvents: "none",
            backgroundColor: "#ffffff",
          }}
          alt="Diagram"
        />
      </MDBox>
    );
  };

  const renderFreeNotesDisplay = (diagramUrl) => {
    if (!diagramUrl) return null;
    return (
      <MDBox
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "700px",
          mx: "auto",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <img
          src={diagramUrl}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            pointerEvents: "none",
            backgroundColor: "#ffffff",
          }}
          alt="Free notes diagram"
        />
      </MDBox>
    );
  };

  return (
    <Card
      sx={{
        mb: 3,
        border: "1px solid #e0e0e0",
        boxShadow: "none",
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <MDBox
        p={2}
        bgColor="info"
        variant="gradient"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          {isAdmin && (
            <IconButton
              size="small"
              onClick={() => onDelete(entry.id)}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#fff",
                "&:hover": { backgroundColor: "#f44336", color: "#fff" },
              }}
            >
              <Icon>delete</Icon>
            </IconButton>
          )}
        </MDBox>
        <MDTypography variant="button" color="white" fontWeight="bold">
          {new Date(entry.createdAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </MDTypography>
      </MDBox>

      <MDBox p={3}>
        <Grid container spacing={3}>
          {/* Diagrams Section */}
          <Grid item xs={12}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="info"
              textTransform="uppercase"
            >
              Body Mapping
            </MDTypography>
            {renderLayeredDisplay(entry.bodyDiagramUrl)}
          </Grid>

          {entry.freeNotesDiagramUrl && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <MDTypography
                variant="caption"
                fontWeight="bold"
                color="info"
                textTransform="uppercase"
              >
                Sketch Notes
              </MDTypography>
              {renderFreeNotesDisplay(entry.freeNotesDiagramUrl)}
            </Grid>
          )}

          {/* Combined Clinical Details Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <MDBox
              sx={{
                backgroundColor: "#fbfbfb",
                borderRadius: "12px",
                border: "1px solid #f0f0f0",
                overflow: "hidden",
              }}
            >
              <MDBox p={2} bgColor="grey-100">
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Clinical Assessment Details
                </MDTypography>
              </MDBox>

              <MDBox p={1}>
                {clinicalFields.map(
                  (field, index) =>
                    field.value && (
                      <MDBox
                        key={index}
                        p={2}
                        sx={{
                          borderBottom:
                            index !==
                            clinicalFields.filter((f) => f.value).length - 1
                              ? "1px solid #eee"
                              : "none",
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="info"
                          display="block"
                          sx={{
                            mb: 0.5,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          {field.label}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontSize: "0.9rem",
                            lineHeight: 1.6,
                          }}
                        >
                          {field.value}
                        </MDTypography>
                      </MDBox>
                    ),
                )}
              </MDBox>
            </MDBox>
          </Grid>

          {/* Footer */}
          <Grid item xs={12}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              pt={2}
              sx={{ borderTop: "1px solid #f0f2f5" }}
            >
              <MDTypography variant="caption" color="text">
                Recorded by: <b>{entry.createdByName || "Unknown"}</b>
              </MDTypography>
              <MDTypography variant="caption" color="text">
                ID: #{entry.id}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
};

DiagnosisEntry.propTypes = {
  entry: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default DiagnosisEntry;
